/**
 * Opportunity Documents Tab Component
 * Displays and manages documents associated with an opportunity
 */

import React, { useState, useRef, useEffect } from 'react';
import { Card, Button, Badge, LoadingSpinner, useToastStore, usePermissions, canWriteDocuments, downloadArtifactWithToast } from '@tracekit/core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAPIClient, deleteArtifact } from '@tracekit/core';
import {
  FileText,
  Upload,
  Download,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  X,
  Info,
  RefreshCw,
  MoreVertical,
  UploadCloud,
} from 'lucide-react';

interface OpportunityDocumentsTabProps {
  opportunityId: string;
  projectId: string;
  currentTrackingStatus?: string; // Current tracking status of the opportunity
  onTrackingStatusChanged?: (newStatus: string) => void; // Callback when tracking status is auto-updated
}

interface RFPDocument {
  artifact_id: string;
  filename: string;
  file_size: number;
  uploaded_at: string;
  processing_status: string;
  chunks_count?: number;
  sections_extracted?: number | null;
  entities_count?: number;
}

interface RFPDocumentsListResponse {
  documents: RFPDocument[];
  total: number;
}

interface RFPDocumentUploadResponse {
  artifact_ids: string[];
  job_id: string;
  status: string;
  message: string;
}

/** Map API/network errors to user-facing upload error messages (KBF-118) */
function getUploadErrorMessage(error: any): string {
  const status = error?.response?.status;
  const detail = (error?.response?.data?.detail ?? error?.response?.data?.message ?? error?.message ?? '').toString().toLowerCase();
  if (status === 413 || detail.includes('file too large') || detail.includes('size') && (detail.includes('exceed') || detail.includes('maximum')) || detail.includes('max file')) {
    return 'File too large. Max 50MB per file.';
  }
  if (status === 415 || detail.includes('invalid type') || detail.includes('unsupported') || detail.includes('invalid file') || detail.includes('file type') || detail.includes('format')) {
    return 'Invalid file type. Use PDF or DOCX.';
  }
  return 'Upload failed. Please try again.';
}

export function OpportunityDocumentsTab({
  opportunityId,
  projectId,
  currentTrackingStatus,
  onTrackingStatusChanged,
}: OpportunityDocumentsTabProps) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [reingestionId, setReingestionId] = useState<string | null>(null);  // Track which artifact is being re-ingested
  const [reingestionError, setReingestionError] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);  // Track which dropdown menu is open
  const [replacingId, setReplacingId] = useState<string | null>(null);  // Track which document is being replaced
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const menuRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const api = getAPIClient();
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  const { hasAnyPermission, isLoading: permissionsLoading } = usePermissions();
  const canWriteDocs = !permissionsLoading && canWriteDocuments(hasAnyPermission);

  // Fetch documents - matches Trace API response format
  const { data: documentsData, isLoading } = useQuery<RFPDocumentsListResponse>({
    queryKey: ['opportunity-documents', opportunityId, projectId],
    queryFn: async () => {
      try {
        // API client's get() method returns response.data directly (not the full axios response)
        // So 'data' here is already the unwrapped response data
        const data = await api.get<RFPDocumentsListResponse>(
          `/api/opportunities/${opportunityId}/rfp-documents`,
          {
            params: { project_id: projectId },
          }
        );
        
        console.log('[DocumentsTab] Fetched documents data:', data);
        
        // Validate structure matches expected format
        if (!data || !data.documents || !Array.isArray(data.documents)) {
          console.warn('[DocumentsTab] Invalid response format, using empty documents');
          return { documents: [], total: 0 };
        }
        return data;
      } catch (error: any) {
        console.error('[DocumentsTab] Failed to fetch documents:', error);
        // Return empty structure on error to prevent React Query undefined error
        return { documents: [], total: 0 };
      }
    },
    enabled: !!opportunityId && !!projectId,
  });

  // Upload mutation - matches Trace API upload response format
  const uploadMutation = useMutation<RFPDocumentUploadResponse, Error, FileList>({
    mutationFn: async (files: FileList) => {
      try {
        const formData = new FormData();
        Array.from(files).forEach((file) => {
          formData.append('files', file);
        });

        console.log('[DocumentsTab] Uploading files:', Array.from(files).map(f => f.name));
        
        // API client's post() method returns response.data directly (not the full axios response)
        // So 'data' here is already the unwrapped response data
        const data = await api.post<RFPDocumentUploadResponse>(
          `/api/opportunities/${opportunityId}/rfp-documents/upload`,
          formData,
          {
            params: { project_id: projectId },
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        
        console.log('[DocumentsTab] API response data:', data);
        console.log('[DocumentsTab] Response data type:', typeof data);
        
        // Validate response matches Trace API format
        if (!data) {
          console.error('[DocumentsTab] API returned null/undefined response');
          throw new Error('Empty response from upload API');
        }
        // Check for required fields - artifact_ids and job_id are required
        if (!Array.isArray(data.artifact_ids) || data.artifact_ids.length === 0) {
          console.error('[DocumentsTab] Invalid response - missing or empty artifact_ids:', data);
          throw new Error('Invalid upload response - missing artifact_ids');
        }
        if (!data.job_id) {
          console.error('[DocumentsTab] Invalid response - missing job_id:', data);
          throw new Error('Invalid upload response - missing job_id');
        }
        // Success - return the data
        console.log('[DocumentsTab] Upload response validated successfully:', { 
          artifact_ids: data.artifact_ids, 
          job_id: data.job_id, 
          status: data.status,
          message: data.message 
        });
        return data;
      } catch (error: any) {
        console.error('[DocumentsTab] Upload mutation error:', error);
        console.error('[DocumentsTab] Error type:', error.constructor.name);
        console.error('[DocumentsTab] Error message:', error.message);
        console.error('[DocumentsTab] Error response:', error.response);
        // Re-throw to let React Query handle it
        throw error;
      }
    },
    onSuccess: async (response: RFPDocumentUploadResponse) => {
      try {
        console.log('[DocumentsTab] Upload successful - onSuccess called:', response);
        // The upload response contains artifact_ids and job_id
        // We need to refetch the documents list to get the full document details
        queryClient.invalidateQueries({ queryKey: ['opportunity-documents', opportunityId, projectId] });
        // Wait a bit for the backend to commit, then refetch multiple times to ensure we get the data
        setTimeout(() => {
          queryClient.refetchQueries({ queryKey: ['opportunity-documents', opportunityId, projectId] });
        }, 300);
        setTimeout(() => {
          queryClient.refetchQueries({ queryKey: ['opportunity-documents', opportunityId, projectId] });
        }, 1000);
        
        // Auto-update tracking status to 'watching' if not already set
        if (currentTrackingStatus !== 'watching' && currentTrackingStatus !== 'bidding' && currentTrackingStatus !== 'won' && currentTrackingStatus !== 'lost') {
          try {
            console.log('[DocumentsTab] Auto-updating tracking status to watching');
            await api.put(
              `/entities/${opportunityId}`,
              { data: { tracking_status: 'watching' } },
              { params: { project_id: projectId } }
            );
            
            // Notify parent component
            onTrackingStatusChanged?.('watching');
            
            // Invalidate opportunities queries to update the list
            queryClient.invalidateQueries({ queryKey: ['govt-opportunities-all', projectId] });
            
            // Show success toast
            addToast({
              title: 'Status Updated',
              message: 'Opportunity automatically marked as "Watching" after document upload',
              type: 'success',
            });
            
            console.log('[DocumentsTab] Tracking status updated to watching');
          } catch (trackingError: any) {
            console.error('[DocumentsTab] Failed to update tracking status:', trackingError);
            // Don't block the upload success - just log the error
          }
        }
        
        setUploading(false);
        console.log('[DocumentsTab] Upload success handler completed');
      } catch (error: any) {
        console.error('[DocumentsTab] Error in onSuccess handler:', error);
        setUploading(false);
        // Don't show error alert here - upload was successful, just refetch failed
      }
    },
    onError: (error: any) => {
      console.error('[DocumentsTab] Upload failed:', error);
      setUploading(false);
      setUploadError(getUploadErrorMessage(error));
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploading(true);
      uploadMutation.mutate(e.target.files);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setUploadError(null);
      setUploading(true);
      const files = e.dataTransfer.files;
      uploadMutation.mutate(files);
    }
  };

  const handleRemoveDocument = async (doc: RFPDocument) => {
    if (!window.confirm(`Remove "${doc.filename}" from this opportunity?`)) return;
    setRemovingId(doc.artifact_id);
    try {
      await deleteArtifact(doc.artifact_id);
      queryClient.invalidateQueries({ queryKey: ['opportunity-documents', opportunityId, projectId] });
      // Sync Mail Draft attachments: only documents in this section should appear there
      queryClient.invalidateQueries({ queryKey: ['email-draft', opportunityId, projectId] });
    } catch (err) {
      console.error('[DocumentsTab] Failed to remove document:', err);
      setUploadError('Failed to remove document. Please try again.');
    } finally {
      setRemovingId(null);
    }
  };

  const handleReIngestDocument = async (doc: RFPDocument) => {
    console.log('[DocumentsTab] Starting re-ingestion for artifact:', doc.artifact_id);
    setReingestionId(doc.artifact_id);
    setReingestionError(null);
    setOpenMenuId(null); // Close dropdown
    
    try {
      const response = await api.post<RFPDocumentUploadResponse>(
        `/api/opportunities/${opportunityId}/rfp-documents/${doc.artifact_id}/re-ingest`,
        {},
        {
          params: { project_id: projectId },
        }
      );
      
      console.log('[DocumentsTab] Re-ingest response:', response);
      
      // Show success toast
      addToast({
        title: 'Re-ingestion Started',
        message: `${doc.filename} is being re-processed. This may take a few minutes.`,
        type: 'success',
      });
      
      // Refetch documents to show updated status
      setTimeout(() => {
        queryClient.refetchQueries({ queryKey: ['opportunity-documents', opportunityId, projectId] });
      }, 500);
      
      console.log('[DocumentsTab] ✅ Re-ingest queued successfully - job_id:', response.job_id);
    } catch (error: any) {
      console.error('[DocumentsTab] Re-ingest failed:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to re-ingest document. Please try again.';
      setReingestionError(errorMessage);
      addToast({
        title: 'Re-ingestion Failed',
        message: errorMessage,
        type: 'error',
      });
    } finally {
      setReingestionId(null);
    }
  };

  const handleReplaceDocument = async (doc: RFPDocument, newFile: File) => {
    console.log('[DocumentsTab] Replacing document:', doc.filename, 'with:', newFile.name);
    setReplacingId(doc.artifact_id);
    setOpenMenuId(null); // Close dropdown
    
    try {
      // Step 1: Remove the old document
      await deleteArtifact(doc.artifact_id);
      console.log('[DocumentsTab] Old document removed');
      
      // Step 2: Upload the new file
      const formData = new FormData();
      formData.append('files', newFile);
      
      const response = await api.post<RFPDocumentUploadResponse>(
        `/api/opportunities/${opportunityId}/rfp-documents/upload`,
        formData,
        {
          params: { project_id: projectId },
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      console.log('[DocumentsTab] New document uploaded:', response);
      
      addToast({
        title: 'Document Replaced',
        message: `${doc.filename} has been replaced with ${newFile.name}. Processing started.`,
        type: 'success',
      });
      
      // Refetch documents
      queryClient.invalidateQueries({ queryKey: ['opportunity-documents', opportunityId, projectId] });
      queryClient.invalidateQueries({ queryKey: ['email-draft', opportunityId, projectId] });
    } catch (error: any) {
      console.error('[DocumentsTab] Replace failed:', error);
      const errorMessage = getUploadErrorMessage(error);
      addToast({
        title: 'Replace Failed',
        message: errorMessage,
        type: 'error',
      });
    } finally {
      setReplacingId(null);
    }
  };

  const handleFileReplaceSelect = (doc: RFPDocument, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleReplaceDocument(doc, files[0]);
    }
    // Reset the input value so the same file can be selected again
    e.target.value = '';
  };

  // Close dropdown menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuId && menuRefs.current[openMenuId]) {
        const menuElement = menuRefs.current[openMenuId];
        if (menuElement && !menuElement.contains(event.target as Node)) {
          setOpenMenuId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuId]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <Badge variant="success" className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
            <CheckCircle className="w-3 h-3 mr-1" />
            Processed
          </Badge>
        );
      case 'processing':
        return (
          <Badge variant="warning" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
            <Clock className="w-3 h-3 mr-1" />
            Processing
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="default" className="bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="error" className="bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
            <AlertCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      default:
        return (
          <Badge variant="default">
            {status}
          </Badge>
        );
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      {/* Flow hint: upload first, then use Email team tab */}
      <div className="p-3 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-700">
        <p className="text-sm text-primary-800 dark:text-primary-200 flex items-start gap-2">
          <Info className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
          <span>Upload RFP documents here first. They are used to generate the email draft in the <strong>Email team</strong> tab. Use the <strong>⋮</strong> menu to download, replace, re-ingest (for 0 chunks), or remove documents.</span>
        </p>
      </div>
      {/* Upload Section */}
      <Card className="p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text">Upload Documents</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Upload RFP and addendums here.</p>
        </div>

        {uploadError && (
          <div className="mb-4 flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{uploadError}</span>
            <button
              type="button"
              onClick={() => setUploadError(null)}
              className="ml-auto p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {canWriteDocs ? (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/10'
              : 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <Upload className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <p className="text-sm text-gray-600 dark:text-theme-text-secondary mb-2">
            Drag and drop RFP documents here, or click to browse
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
            Supported formats: PDF, DOCX (Max 50MB per file)
          </p>
          <input
            type="file"
            id="file-upload"
            multiple
            accept=".pdf,.docx"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
          <label htmlFor="file-upload" className="cursor-pointer">
            <Button
              variant="primary"
              disabled={uploading}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('file-upload')?.click();
              }}
            >
              {uploading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Select Files
                </>
              )}
            </Button>
          </label>
        </div>
        ) : (
          <p className="text-sm text-gray-600 dark:text-theme-text-secondary border border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
            You have read-only access to documents. Uploading and removing files requires documents write permission.
          </p>
        )}
      </Card>

      {/* Documents List */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text">
            Documents ({documentsData?.total || 0})
          </h3>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : !documentsData || documentsData.documents.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="w-12 h-12 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-theme-text-secondary">
              No documents uploaded yet. Upload RFP documents to get started.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {documentsData.documents.map((doc) => (
              <div
                key={doc.artifact_id}
                className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                <div className="flex items-center gap-4 flex-1">
                  <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">
                      {doc.filename}
                    </p>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 dark:text-theme-text-secondary">
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span>•</span>
                      <span>Uploaded {formatDate(doc.uploaded_at)}</span>
                      {doc.chunks_count !== undefined && (
                        <>
                          <span>•</span>
                          <span>{doc.chunks_count} chunks</span>
                        </>
                      )}
                      {doc.entities_count !== undefined && doc.entities_count > 0 && (
                        <>
                          <span>•</span>
                          <span>{doc.entities_count} entities</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(doc.processing_status)}
                  
                  {/* Compact Actions: download always; mutate actions only with documents write */}
                  <div className="relative" ref={(el) => (menuRefs.current[doc.artifact_id] = el)}>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setOpenMenuId(openMenuId === doc.artifact_id ? null : doc.artifact_id)}
                      disabled={removingId === doc.artifact_id || reingestionId === doc.artifact_id || replacingId === doc.artifact_id}
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      aria-label="Document actions"
                      title="Document actions"
                    >
                      {removingId === doc.artifact_id || reingestionId === doc.artifact_id || replacingId === doc.artifact_id ? (
                        <LoadingSpinner size="sm" className="w-4 h-4" />
                      ) : (
                        <MoreVertical className="w-4 h-4" />
                      )}
                    </Button>

                    {openMenuId === doc.artifact_id && (
                      <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50">
                        <div className="py-1" role="menu">
                          <button
                            onClick={() => {
                              downloadArtifactWithToast(doc.artifact_id, doc.filename, addToast);
                              setOpenMenuId(null);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                            role="menuitem"
                          >
                            <Download className="w-4 h-4 mr-3" />
                            Download
                          </button>

                          {canWriteDocs && (
                            <>
                          <button
                            onClick={() => {
                              const input = fileInputRefs.current[doc.artifact_id];
                              if (input) {
                                input.click();
                              }
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                            role="menuitem"
                          >
                            <UploadCloud className="w-4 h-4 mr-3" />
                            Replace
                          </button>
                          <input
                            ref={(el) => (fileInputRefs.current[doc.artifact_id] = el)}
                            type="file"
                            accept=".pdf,.docx"
                            onChange={(e) => handleFileReplaceSelect(doc, e)}
                            className="hidden"
                          />

                          {(doc.chunks_count === 0 || doc.chunks_count === undefined) && 
                           (doc.processing_status === 'completed' || doc.processing_status === 'failed') && (
                            <button
                              onClick={() => handleReIngestDocument(doc)}
                              className="flex items-center w-full px-4 py-2 text-sm text-orange-600 dark:text-orange-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                              role="menuitem"
                            >
                              <RefreshCw className="w-4 h-4 mr-3" />
                              Re-ingest
                            </button>
                          )}

                          <div className="border-t border-gray-100 dark:border-gray-700 my-1" />

                          <button
                            onClick={() => {
                              handleRemoveDocument(doc);
                              setOpenMenuId(null);
                            }}
                            className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                            role="menuitem"
                          >
                            <Trash2 className="w-4 h-4 mr-3" />
                            Remove
                          </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
